<template>
  <form>
    <v-row align="center" justify="center">
      <v-col cols="12" sm="10" md="6">
        <v-card class="elevation-12">
          <v-toolbar color="teal darken-4" dark flat>
            <v-toolbar-title>Client Signup form</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn color="green" v-on="on" to="/login">
                  <span>Login</span>
                  <v-icon>mdi-login</v-icon>
                </v-btn>
              </template>
              <span>Got an existing account? Login to your account</span>
            </v-tooltip>
          </v-toolbar>

          <!-- Pre-signup policy notes -->
          <v-card-text>
            <v-card class="my-6 px-2 elevation-1" outlined>
              <v-card-title>
                <span class="font-weight-light">Important Notes</span>
                <v-spacer></v-spacer>
                <v-icon>mdi-information-outline</v-icon>
              </v-card-title>
              <v-card-subtitle>
                <span class="font-weight-light"
                  >Read this before creating your account.</span
                >
              </v-card-subtitle>
              <v-card-text>
                <v-list dense>
                  <v-list-item-group>
                    <v-list-item
                      v-for="(note, index) in important_notes"
                      :key="index"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-light">
                          {{ index + 1 }}. {{ note }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list-item-group>
                </v-list>
              </v-card-text>
            </v-card>

            <!-- Login credentials and valid ID upload -->
            <v-card class="elevation-1" outlined>
              <v-list-item>
                <div class="overline">
                  <span><v-icon>mdi-account-check</v-icon> User Account</span>
                </div>
              </v-list-item>
              <v-list-item>
                <picture-input
                  ref="pictureInput"
                  width="300"
                  height="200"
                  margin="16"
                  accept="image/jpeg,image/png"
                  size="10"
                  button-class="btn"
                  :custom-strings="{
                    upload: '<h1=>Valid ID</h1>',
                    drag: 'Input valid ID 😺 --You could also do drag and drop)',
                  }"
                  @change="onChange"
                >
                </picture-input>
              </v-list-item>
              <v-list-item class="justify-center">
                <div class="error-msg" v-if="$v.profile_image_url.$dirty && !$v.profile_image_url.required">
                  Please upload a valid government-issued ID.
                </div>
              </v-list-item>
              <!-- userId accepts email or PH mobile number (09XXXXXXXXX) -->
              <v-list-item>
                <v-text-field
                  prepend-icon="mdi-account-circle-outline"
                  v-model.lazy="userId"
                  label="User ID"
                  :error-messages="userIdErrors"
                  placeholder="Please enter Email or Mobile Number"
                  required
                  @blur="$v.userId.$touch()"
                ></v-text-field>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  prepend-icon="mdi-shield-check-outline"
                  v-model="password"
                  :type="'password'"
                  :error-messages="passwordErrors"
                  label="Password"
                  required
                  @input="$v.password.$touch()"
                  @blur="$v.password.$touch()"
                ></v-text-field>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  prepend-icon="mdi-shield-check"
                  v-model.lazy="confirmPassword"
                  :type="'password'"
                  :error-messages="confirmPasswordErrors"
                  label="Confirm Password"
                  required
                  @input="$v.confirmPassword.$touch()"
                  @blur="$v.confirmPassword.$touch()"
                ></v-text-field>
              </v-list-item>
            </v-card>

            <!-- Personal details of the client -->
            <v-card class="mt-8 elevation-1" outlined>
              <v-list-item>
                <div class="overline">
                  <span
                    ><v-icon>mdi-file</v-icon>
                    CLIENT INFORMATION
                  </span>
                </div>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  v-model="lastName"
                  :error-messages="lastNameErrors"
                  label="Last Name"
                  required
                  @input="$v.lastName.$touch()"
                  @blur="$v.lastName.$touch()"
                ></v-text-field>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  v-model="firstName"
                  :error-messages="firstNameErrors"
                  label="First Name"
                  required
                  @input="$v.firstName.$touch()"
                  @blur="$v.firstName.$touch()"
                ></v-text-field>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  v-model="middleName"
                  label="Middle Name"
                ></v-text-field>
              </v-list-item>
              <!-- Optional secondary contact; not used as login credential -->
              <v-list-item>
                <v-text-field
                  v-model="email"
                  label="Email"
                  placeholder="juan.delacruz@gmail.com"
                ></v-text-field>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  v-model="mobileNumber"
                  :error-messages="mobileNumberErrors"
                  label="Mobile Number"
                  placeholder="09171234567"
                  required
                  @change="$v.mobileNumber.$touch()"
                  @blur="$v.mobileNumber.$touch()"
                ></v-text-field>
              </v-list-item>

              <v-list-item>
                <v-select
                  v-model="gender"
                  :items="gender_list"
                  :error-messages="genderErrors"
                  label="Gender"
                  required
                  @change="$v.gender.$touch()"
                  @blur="$v.gender.$touch()"
                ></v-select>
              </v-list-item>
              <v-list-item>
                <v-select
                  v-model="civilStatus"
                  :items="civilStatusList"
                  :error-messages="civilStatusErrors"
                  label="Civil Status"
                  required
                  @change="$v.civilStatus.$touch()"
                  @blur="$v.civilStatus.$touch()"
                ></v-select>
              </v-list-item>
              <v-list-item>
                <v-select
                  v-model="educationalAttainment"
                  :items="educationalAttainmentList"
                  :error-messages="educationalAttainmentErrors"
                  label="Educational Attainment"
                  required
                  @change="$v.educationalAttainment.$touch()"
                  @blur="$v.educationalAttainment.$touch()"
                ></v-select>
              </v-list-item>
              <!-- Free-text fallback when "Others" is selected -->
              <v-list-item v-if="educationalAttainment == 'Others'">
                <v-text-field
                  v-model="otherEducationalAttainment"
                  label="Other educational attainment"
                  :error-messages="otherEducationalAttainmentErrors"
                  required
                  @input="$v.otherEducationalAttainment.$touch()"
                  @blur="$v.otherEducationalAttainment.$touch()"
                ></v-text-field>
              </v-list-item>
              <!-- Birthdate errors rendered separately because date-picker doesn't accept error-messages prop -->
              <v-list-item>
                <v-row>
                  <v-col cols="6" sm="6" md="6">
                    <date-picker
                      v-model="birthdate"
                      placeholder="Birthday: YYYY-MM-DD"
                      format="YYYY-MM-DD"
                      :error-messages="birthdateErrors"
                      type="date"
                      required
                      @change="$v.birthdate.$touch()"
                      @blur="$v.birthdate.$touch()"
                    ></date-picker>
                  </v-col>
                  <v-col cols="6" sm="6" md="6">
                    <ul style="list-style-type: none" class="red--text">
                      <li v-for="bError in birthdateErrors" :key="bError">
                        {{ bError }}
                      </li>
                    </ul>
                  </v-col>
                </v-row>
              </v-list-item>
              <v-list-item>
                <v-text-field
                  v-model="disability"
                  label="Disability"
                  placeholder="Type of Disability (If Any)"
                ></v-text-field>
              </v-list-item>

              <v-list-item>
                <v-textarea
                  color="gray"
                  label="Address"
                  v-model="address"
                  :error-messages="addressErrors"
                  required
                  @input="$v.address.$touch()"
                  @blur="$v.address.$touch()"
                ></v-textarea>
              </v-list-item>
            </v-card>
          </v-card-text>

          <v-card-actions>
            <v-btn @click="clear">
              <span>
                <v-icon>mdi-close-circle-outline</v-icon>
                clear
              </span>
            </v-btn>
            <v-spacer></v-spacer>

            <!-- Dialog handles form validation, uniqueness checks, OTP flow, and final submit -->
            <v-dialog v-model="dialog" persistent max-width="290">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="orange darken-4"
                  dark
                  @click="generate_otp"
                  v-bind="attrs"
                  v-on="on"
                >
                  Next
                </v-btn>
              </template>
              <v-card v-if="invalidForm == true">
                <v-card-text>
                  <sweetalert-icon icon="error" />
                </v-card-text>
                <v-card-title class="text-h5">
                  Please fill in the form correctly and completely
                </v-card-title>
                <v-card-actions>
                  <v-spacer />
                  <v-btn color="grey darken-1" text @click="dialog = false">
                    Close
                  </v-btn>
                  <v-spacer />
                </v-card-actions>
              </v-card>
              <v-card v-if="otpState == 'Failed'">
                <v-card-text>
                  <sweetalert-icon icon="error" />
                </v-card-text>
                <v-card-text class="text-h5">
                  Oooppss... <br />
                  <div v-if="!otpErrorMessage">
                    Some glitch or error happened.
                  </div>
                  <div v-if="otpErrorMessage">
                    {{ otpErrorMessage }}
                  </div>
                </v-card-text>
                <v-card-actions>
                  <v-btn color="grey darken-1" text @click="dialog = false">
                    Close
                  </v-btn>
                  <v-spacer />
                  <v-btn color="green darken-1" text @click="generate_otp">
                    Retry
                  </v-btn>
                </v-card-actions>
              </v-card>

              <v-card v-if="otpState == 'Sending'">
                <v-card-title> Sending OTP... </v-card-title>
                <v-card-text>
                  <Spinner size="huge" />
                </v-card-text>
              </v-card>

              <v-card v-if="otpState == 'Success'">
                <v-card-title class="text-h5"> OTP Verification </v-card-title>
                <v-card-text>
                  <span class="font-weight-light">
                    Please input OTP sent to
                  </span>
                  <span class="font-weight-medium">
                    {{ mobileNumber }}
                  </span>
                </v-card-text>
                <v-card-text>
                  <v-text-field
                    type="number"
                    :min="6"
                    :max="6"
                    v-model="otp_code"
                    label="6 Digit OTP"
                    required
                  ></v-text-field>
                </v-card-text>
                <v-card-subtitle v-if="otpErrorMessage" class="text-center">
                  <span class="font-italic red--text">{{
                    otpErrorMessage
                  }}</span
                  ><br />
                  <span class="font-weight-light red--text"
                    >Click "Resend OTP" to resend</span
                  >
                </v-card-subtitle>
                <v-card-actions v-if="otpErrorMessage">
                  <v-spacer />
                  <v-btn color="orange darken-5" dark @click="generate_otp">
                    <span> Resend OTP?</span>
                  </v-btn>
                  <v-spacer />
                </v-card-actions>
                <v-card-actions>
                  <v-btn color="green darken-1" text @click="dialog = false">
                    Cancel
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn color="green darken-5" dark @click="submit">
                    <span>
                      submit
                      <v-icon>mdi-send</v-icon>
                    </span>
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </form>
</template>

<script>
import PictureInput from "vue-picture-input";
import { validationMixin } from "vuelidate";

import { EventBus } from "../utils";
import logger, { describeApiError } from "../utils/logger";
import {
  post_image,
  post_user,
  generate_service_user_otp,
  check_username,
  check_mobile_num,
} from "../api";

import { required, minLength, maxLength } from "vuelidate/lib/validators";
import Spinner from "vue-simple-spinner";
import DatePicker from "vue2-datepicker";

export default {
  mixins: [validationMixin],
  components: {
    Spinner,
    DatePicker,
    PictureInput,
  },
  // All uniqueness checks are deferred to generate_otp to avoid per-keystroke API calls
  validations() {
    let default_validation = {
      lastName: { required, minLength: minLength(2) },
      firstName: { required, minLength: minLength(2) },
      userId: {
        required,
        // Accepts a valid email OR a PH mobile number (09XXXXXXXXX)
        validFormat(value) {
          if (!value) return true;
          const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
          const isMobile = /^(09)\d{9}$/.test(value);
          return isEmail || isMobile;
        },
      },
      gender: { required },
      civilStatus: { required },
      password: { required },
      birthdate: {
        required,
        // Minimum age is 18
        validateAge() {
          var age = this.getAge();
          if (age > 17) return true;

          return false;
        },
      },
      mobileNumber: {
        required,
        minLength: minLength(11),
        maxLength: maxLength(11),
        validFormat(value) {
          return /^(09)\d{9}$/.test(value);
        },
      },
      educationalAttainment: { required },
      confirmPassword: { required },
      address: { required },
      profile_image_url: { required },
    };
    // otherEducationalAttainment is only required when "Others" is selected
    if (this.educationalAttainment === "Others") {
      default_validation["otherEducationalAttainment"] = { required };
    }
    return default_validation;
  },
  data: () => ({
    profile_image_url: "",
    birthdate_menu: "",
    birthdate: null,
    lastName: "",
    firstName: "",
    middleName: "",
    mobileNumber: "",
    email: "",
    gender: "",
    disability: "",
    civilStatus: "",
    civilStatusList: ["Single", "Married", "Widowed", "Divorced"],
    password: "",
    confirmPassword: "",
    classification: "",
    userId: "",
    classificationList: ["OFW / OF", "Health Care Provider", "LSI", "Others"],
    address: "",
    gender_list: ["Male", "Female", "Preferred not to say"],
    otp_code: "",
    // Possible states: Init | Sending | Success | Failed
    otpState: "Init",
    role: "User",
    otpErrorMessage: "",
    // Set by generate_otp after uniqueness check; cleared when user edits the field
    userIdTaken: false,
    mobileNumberTaken: false,
    invalidForm: true,
    dialog: false,
    failedSubmit: false,
    educationalAttainment: "",
    educationalAttainmentList: [
      "Elementary Graduate",
      "High School Graduate",
      "Bachelor's Degree",
      "Master's Degree",
      "Doctorate Degree",
      "Others",
    ],
    otherEducationalAttainment: "",
    reason: "",
    important_notes: [
      "A total of 3 consultations only will be provided for each service user.",
      "Be mindful when choosing and confirming your appointment slots, and refrain from cancellations.",
      "TMH provides psychological support only and is not intended for psychiatric diagnosis, treatment, or therapy.",
      "No prescriptions, medical certificates, or medical abstracts will be provided.",
      "TMH does not cater to NP screenings, clearances, or legal/court cases.",
      "Creating multiple accounts is prohibited. If assisting someone, register their info with their consent.",
      "Only individuals 19 years and older are eligible to book.",
    ],
  }),
  watch: {
    // Clear stale "already taken" errors when the user edits the field
    userId() {
      this.userIdTaken = false;
    },
    mobileNumber() {
      this.mobileNumberTaken = false;
    },
  },
  computed: {
    birthdateErrors() {
      const errors = [];
      if (!this.$v.birthdate.$dirty) return errors;
      !this.$v.birthdate.required && errors.push("Birthdate is required.");
      !this.$v.birthdate.validateAge &&
        errors.push("You must be at least 18 years old to register.");
      return errors;
    },
    genderErrors() {
      const errors = [];
      if (!this.$v.gender.$dirty) return errors;
      !this.$v.gender.required && errors.push("Please select a gender.");
      return errors;
    },
    civilStatusErrors() {
      const errors = [];
      if (!this.$v.civilStatus.$dirty) return errors;
      !this.$v.civilStatus.required && errors.push("Please select a civil status.");
      return errors;
    },
    firstNameErrors() {
      const errors = [];
      if (!this.$v.firstName.$dirty) return errors;
      !this.$v.firstName.required && errors.push("First name is required.");
      !this.$v.firstName.minLength && errors.push("First name must be at least 2 characters.");
      return errors;
    },
    lastNameErrors() {
      const errors = [];
      if (!this.$v.lastName.$dirty) return errors;
      !this.$v.lastName.required && errors.push("Last name is required.");
      !this.$v.lastName.minLength && errors.push("Last name must be at least 2 characters.");
      return errors;
    },
    userIdErrors() {
      const errors = [];
      if (!this.$v.userId.$dirty) return errors;
      !this.$v.userId.required &&
        errors.push("Email or mobile number is required.");
      !this.$v.userId.validFormat &&
        errors.push("Enter a valid email address or PH mobile number (e.g. 09171234567).");
      this.userIdTaken && errors.push("This email or mobile number is already registered.");
      return errors;
    },
    passwordErrors() {
      const errors = [];
      if (!this.$v.password.$dirty) return errors;
      !this.$v.password.required && errors.push("Password is required.");
      return errors;
    },
    confirmPasswordErrors() {
      const errors = [];
      if (!this.$v.confirmPassword.$dirty) return errors;
      !this.$v.confirmPassword.required && errors.push("Please confirm your password.");
      this.confirmPassword &&
        this.confirmPassword !== this.password &&
        errors.push("Passwords do not match.");
      return errors;
    },
    mobileNumberErrors() {
      const errors = [];
      if (!this.$v.mobileNumber.$dirty) return errors;
      !this.$v.mobileNumber.required &&
        errors.push("Mobile number is required.");
      (!this.$v.mobileNumber.minLength || !this.$v.mobileNumber.maxLength) &&
        errors.push("Mobile number must be exactly 11 digits.");
      !this.$v.mobileNumber.validFormat &&
        errors.push("Mobile number must start with 09 (e.g. 09171234567).");
      this.mobileNumberTaken && errors.push("This mobile number is already registered.");
      return errors;
    },
    addressErrors() {
      const errors = [];
      if (!this.$v.address.$dirty) return errors;
      !this.$v.address.required && errors.push("Address is required.");
      return errors;
    },
    educationalAttainmentErrors() {
      const errors = [];
      if (!this.$v.educationalAttainment.$dirty) return errors;
      !this.$v.educationalAttainment.required &&
        errors.push("Please select your educational attainment.");
      return errors;
    },
    otherEducationalAttainmentErrors() {
      const errors = [];
      if (!this.$v.otherEducationalAttainment.$dirty) return errors;
      !this.$v.otherEducationalAttainment.required &&
        errors.push("Please specify your educational attainment.");
      return errors;
    },
  },
  methods: {
    onChange(image) {
      this.$v.profile_image_url.$touch();
      if (image) {
        post_image(image)
          .then((response) => {
            this.profile_image_url = response.data.url;
          })
          .catch((error) => {
            // A failed upload used to be silently lost. Clear the field so the
            // required-image validation flags it and the user re-uploads.
            logger.error("Signup", "Image upload failed", describeApiError(error));
            this.profile_image_url = "";
          });
      } else {
        this.profile_image_url = "";
      }
    },
    // Validates the form, checks userId/mobileNumber uniqueness, then sends OTP
    async generate_otp() {
      this.$v.$touch();
      this.otpErrorMessage = "";
      this.otp_code = "";
      this.userIdTaken = false;
      this.mobileNumberTaken = false;

      if (this.$v.$invalid) {
        this.invalidForm = true;
        logger.debug("Signup", "Validation failed:", JSON.parse(JSON.stringify(this.$v)));
        return;
      }

      // Run both uniqueness checks in parallel — only reached when local validation passes
      let usernameRes, mobileRes;
      try {
        [usernameRes, mobileRes] = await Promise.all([
          check_username({ username: this.userId }),
          check_mobile_num({ mobile_number: this.mobileNumber }),
        ]);
      } catch (error) {
        // A failed uniqueness check used to reject unhandled and leave the dialog
        // hanging. Surface it in the same Failed card so the user can retry.
        logger.error("Signup", "Uniqueness check failed", describeApiError(error));
        this.otpErrorMessage =
          "Can't reach the server. Check your connection and try again.";
        this.otpState = "Failed";
        return;
      }

      this.userIdTaken = !usernameRes.data.status;
      this.mobileNumberTaken = !mobileRes.data.status;

      logger.debug("Signup", "Uniqueness check — userIdTaken:", this.userIdTaken, "| mobileNumberTaken:", this.mobileNumberTaken);

      if (this.userIdTaken || this.mobileNumberTaken) {
        this.invalidForm = true;
        return;
      }

      this.invalidForm = false;
      this.otpState = "Sending";
      generate_service_user_otp({ mobile_number: this.mobileNumber })
        .then(() => {
          this.otpState = "Success";
        })
        .catch((err) => {
          logger.error("Signup", "Failed to generate OTP", describeApiError(err));
          if (err.response) {
            if (err.response.status == 429) {
              this.otpErrorMessage = "Max retry for OTP already reached";
            }
          }
          this.otpState = "Failed";
        });
    },

    getAge() {
      var today = new Date();
      var birthDate = new Date(this.birthdate);
      var age = today.getFullYear() - birthDate.getFullYear();
      var m = today.getMonth() - birthDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    },

    to_list_string(data) {
      // Filter out empty-string entries
      let result = [];
      for (var d of data) {
        if (d.value === "") {
          continue;
        }
        result.push(d.value);
      }
      return result;
    },
    submit() {
      this.otpErrorMessage = "";
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.invalidForm = true;
      } else {
        this.invalidForm = false;
        const user_account = {
          profile_image_url: this.profile_image_url,
          last_name: this.lastName,
          middle_name: this.middleName,
          first_name: this.firstName,
          username: this.userId,
          password: this.password,
          role: this.role,
          mobile_number: this.mobileNumber,
          email: this.email,
        };

        const client_details = {
          birthdate: this.birthdate,
          affiliation: this.affiliation,
          address: this.address,
          gender: this.gender,
          civil_status: this.civilStatus,
          // Falls back to free-text when "Others" was selected
          educational_attainment:
            this.educationalAttainment || this.otherEducationalAttainment,
          reason: "N/A",
          disability: this.disability,
        };

        post_user({ user_account, client_details, otp_code: this.otp_code })
          .then(() => this.$router.push("/registration_complete"))
          .catch((err) => {
            logger.error("Signup", "Failed to submit registration", describeApiError(err));
            if (err.response && err.response.status == 409) {
              this.otpErrorMessage = "OTP provided is invalid";
            } else if (err.response && err.response.status == 410) {
              this.otpErrorMessage = "OTP already expired.";
            } else {
              this.otpErrorMessage =
                "Something went wrong while submitting your registration. Please try again.";
            }
          });
      }
    },

    clear() {
      this.$v.$reset();
      this.name = "";
      this.email = "";
      this.organization = "";
      this.facility = null;
      this.password = "";
      this.confirmPassword = "";
      this.is_service_provider = false;
    },
  },
  mounted() {
    EventBus.$on("failedRegistering", (msg) => {
      this.errorMsg = msg;
    });
  },
  beforeDestroy() {
    EventBus.$off("failedRegistering");
  },
};
</script>

<style scoped>
.error-msg {
  color: red;
  font-weight: bold;
}
</style>
